import { Modal, Box, Typography, styled, Backdrop } from "@mui/material";
import { useFormik } from "formik";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { changePassword } from "@/services/users.service";
import { changePasswordSchema } from "@/schemas/profile.schema";
import { showToast } from "@/redux/actions/toast-actions";
import { ChangePassword } from "@/pages/profile/pages/_components/change-password";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";

const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.8)" : "rgba(0, 0, 0, 0.5)",
  backdropFilter: "blur(4px)",
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 480,
  maxWidth: "90vw",
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 25px 50px rgba(0,0,0,0.8)"
      : "0 25px 50px rgba(0,0,0,0.15)",
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  outline: "none",
}));

// Mandatory post-login step (passwordChangeRequired) — no close affordance,
// same as OtpModal: the only way out is a successful submit.
const ForceChangePasswordModal = ({ open, email, onSuccess }) => {
  const dispatch = useDispatch();

  const mutation = useMutation({
    mutationFn: changePassword,
    onSuccess: (data) => {
      dispatch(
        showToast(data?.message ?? "Password changed successfully", "success"),
      );
      onSuccess?.();
    },
  });

  const formik = useFormik({
    initialValues: { oldPassword: "", newPassword: "" },
    validationSchema: changePasswordSchema,
    onSubmit: (values) => {
      mutation.mutate({ ...values, phoneOrEmail: email });
    },
  });

  return (
    <Modal open={open} disableEscapeKeyDown slots={{ backdrop: StyledBackdrop }}>
      <ModalBox>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Change Your Password
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Your account requires a password change before you can continue.
          </Typography>
        </Box>

        {mutation.isError && (
          <CustomErrorMessage
            message={
              mutation.error?.message ??
              "Failed to change password. Please try again."
            }
          />
        )}

        <form onSubmit={formik.handleSubmit}>
          <ChangePassword formik={formik} isDisabled={mutation.isPending} />
        </form>
      </ModalBox>
    </Modal>
  );
};

export default ForceChangePasswordModal;
